/// <reference path="../../types/index.d.ts" />

/**
 * Global state management for the game
 *
 * The globals are a stack where new records are added when a new action
 * needs to be executed. This ensures proper scoping of nested action context
 * (object being acted upon, action being taken, additional data, etc.).
 */

export interface LoopArgs {
    object: any;
    idx: number;
    array: any;
    prevObject: any;
}

export class GlobalsStore {
    bRunningTimers: boolean = false;
    bMasterTimer: boolean = false;
    curActions: any = undefined;
    loopObject: any = null;
    loopArgs: LoopArgs = {
        object: null,
        idx: 0,
        array: null,
        prevObject: null
    };
    loopArgsValid: boolean = false;
    inputDataObject: any = null;
    movingDirection: string = "";
    runningLiveTimerCommands: boolean = false;
    theObj: any = null;
    variableGettingSet: any = null;
    advancedInputResolver: ((value: any) => void) | null = null;
    advancedInputReject: ((reason?: any) => void) | null = null;
    advancedScriptDepth: number = 0;
    globalScriptsRan: boolean = false;
    bTimersExecutedThisTurn: boolean = false;
    commandQueueEmptiedSinceCheckpoint: boolean = false;
    hasUnsavedProgress: boolean = false;
    additionalData: any = null;
    actionBeingTaken: any = null;
    objectBeingActedUpon: any = null;

    // Static properties (they keep their value on restore)
    _staticProps: string[] = [
        'bCancelMove',
        'currentImage',
        'endGame',
    ];
    bCancelMove: boolean = false;
    currentImage: string = "";
    endGame: boolean = false;

    // Unique index for debugging
    static _nextIndex: number = 0;
    _index: number = -1;

    constructor(other?: GlobalsStore) {
        this._index = GlobalsStore._nextIndex++;
        if (other !== undefined) {
            for (const name of Object.keys(this)) {
                if (name.startsWith('_')) continue;
                (this as any)[name] = (other as any)[name];
            }
        }
    }
}

export class GlobalsStack {
    entries: GlobalsStore[] = [new GlobalsStore()];

    constructor() {
        // Return a proxy version to be able to use a generic getter/setter function
        return new Proxy(this, {
            get(target, prop, receiver) {
                if (prop in target) {
                    return (target as any)[prop];
                } else {
                    return (target as any).last[prop as keyof GlobalsStore];
                }
            },
            set(target, prop, value, receiver) {
                if (prop in target) {
                    (target as any)[prop] = value;
                    return true;
                } else {
                    ((target as any).last as any)[prop] = value;
                    return true;
                }
            }
        });
    }

    get last(): GlobalsStore {
        if (this.entries.length === 0) {
            this.entries.push(new GlobalsStore());
        }
        return this.entries[this.entries.length - 1];
    }

    get length(): number {
        return this.entries.length;
    }

    store(changes?: Partial<GlobalsStore>): number {
        this.entries.push(new GlobalsStore(this.last));
        const lastEntry = this.last;
        if (changes !== undefined) {
            for (const prop in changes) {
                if (prop in lastEntry) {
                    (lastEntry as any)[prop] = (changes as any)[prop];
                } else {
                    console.warn(`${prop} is not a valid global property`);
                }
            }
        }
        return lastEntry._index;
    }

    /**
     * Restore the globals stack.
     * If no index provided, pop a single nested frame (keep base frame).
     * If index provided, pop frames until that frame is removed.
     * Defensive: never leave stack empty.
     */
    restore(index?: number): GlobalsStore {
        // No index: pop single nested frame if present
        if (index === undefined) {
            if (this.entries.length <= 1) return this.last;
            const popped = this.entries.pop();

            // Ensure top exists
            if (this.entries.length === 0) this.entries.push(new GlobalsStore());

            // Copy static props from popped into new top
            if (popped && Array.isArray((popped as any)._staticProps)) {
                const top = this.last;
                for (const name of (popped as any)._staticProps) {
                    try { (top as any)[name] = (popped as any)[name]; } catch (e) {}
                }
            }

            return this.last;
        }

        // Indexed restore: pop frames until requested index removed.
        let foundPos = -1;
        for (let i = this.entries.length - 1; i >= 0; i--) {
            const e = this.entries[i];
            if (e && e._index === index) { foundPos = i; break; }
        }
        if (foundPos === -1) {
            // Already restored; no-op
            return this.last;
        }

        for (;;) {
            if (this.entries.length === 0) break;
            const topPos = this.entries.length - 1;
            if (topPos < foundPos) break;
            const poppedFrame = this.entries.pop();

            // Ensure there is always at least one frame
            if (this.entries.length === 0) {
                this.entries.push(new GlobalsStore());
                break;
            }

            // Copy static props from popped frame into new top
            try {
                if (poppedFrame && Array.isArray((poppedFrame as any)._staticProps)) {
                    const newTop = this.last;
                    for (const name of (poppedFrame as any)._staticProps) {
                        try { (newTop as any)[name] = (poppedFrame as any)[name]; } catch (e) {}
                    }
                }
            } catch (e) {}
        }

        return this.last;
    }

    reset(): void {
        this.entries.splice(0, this.entries.length, new GlobalsStore());
    }

    // Named findXxx (not objectBeingActedUpon/actionBeingTaken) deliberately:
    // the constructor's Proxy get/set traps check `prop in target` to decide
    // whether to read/write a real property on this class vs. delegating to
    // `this.last[prop]` (the current frame's per-call data field of the SAME
    // name). A method here named exactly `objectBeingActedUpon` would win
    // that check and permanently shadow the per-frame field — every bare
    // `Globals.objectBeingActedUpon` read (no call parens) would silently
    // return this search function itself instead of the frame's actual
    // value, which is exactly what broke `getSelf` (DataCloneError trying to
    // postMessage the function) and `getCurrentObject`.
    findObjectBeingActedUpon(): any {
        for (let i = this.entries.length - 1; i >= 0; i--) {
            const entry = this.entries[i];
            if (entry.objectBeingActedUpon) return entry.objectBeingActedUpon;
        }
        return null;
    }

    findActionBeingTaken(): any {
        for (let i = this.entries.length - 1; i >= 0; i--) {
            const entry = this.entries[i];
            if (entry.actionBeingTaken) return entry.actionBeingTaken;
        }
        return null;
    }
}

export const Globals: any = new GlobalsStack();

/**
 * Clear the loop state at the start of a user interaction. This used to push
 * a copy of the top frame that was never restored, so the Globals stack grew
 * by one frame per interaction. Clearing the fields on the top frame is
 * equivalent (the pushed frame was a copy of it) without the growth.
 */
export function ResetLoopObjects(): number {
    (Globals as any).loopObject = null;
    (Globals as any).loopArgs = {
        object: null,
        idx: 0,
        array: null,
        prevObject: null,
    };
    (Globals as any).loopArgsValid = false;
    return (Globals as any).last._index;
}

export function RestoreLoopObject(index: number): void {
    (Globals as any).restore(index);
}
